import {
    ArrowUpRight,
    BadgeCheck,
    Briefcase,
    Building2,
    ChevronDown,
    Clock,
    DollarSign,
    ExternalLink,
    FileCheck2,
    FileText,
    Filter,
    Globe,
    Handshake,
    Kanban,
    Landmark,
    Layers,
    Linkedin,
    Mail,
    MessageSquarePlus,
    Phone,
    Plus,
    Search,
    ShieldCheck,
    Star,
    Tag,
    Target,
    Trash2,
    TrendingUp,
    User,
    UserPlus,
    Users,
    X
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useGrandeEcole from '../../stores/useGrandeEcole';

// ─── Palette couleurs ────────────────────────────────────────────────────────
const COLOR_MAP = {
  zinc:    { dot:'bg-zinc-500',    text:'text-zinc-400',    border:'border-zinc-700/80',    bg:'bg-zinc-900/30',    pill:'bg-zinc-800 text-zinc-300',        accent:'zinc-400'    },
  sky:     { dot:'bg-sky-500',     text:'text-sky-400',     border:'border-sky-800/60',     bg:'bg-sky-950/20',     pill:'bg-sky-900/60 text-sky-300',        accent:'sky-400'     },
  amber:   { dot:'bg-amber-500',   text:'text-amber-400',   border:'border-amber-800/60',   bg:'bg-amber-950/20',   pill:'bg-amber-900/50 text-amber-300',    accent:'amber-400'   },
  emerald: { dot:'bg-emerald-500', text:'text-emerald-400', border:'border-emerald-800/60', bg:'bg-emerald-950/20', pill:'bg-emerald-900/50 text-emerald-300',accent:'emerald-400' },
  blue:    { dot:'bg-blue-500',    text:'text-blue-400',    border:'border-blue-800/60',    bg:'bg-blue-950/20',    pill:'bg-blue-900/50 text-blue-300',      accent:'blue-400'    },
  purple:  { dot:'bg-purple-500',  text:'text-purple-400',  border:'border-purple-800/60',  bg:'bg-purple-950/20',  pill:'bg-purple-900/50 text-purple-300',  accent:'purple-400'  },
  red:     { dot:'bg-red-500',     text:'text-red-400',     border:'border-red-800/60',     bg:'bg-red-950/20',     pill:'bg-red-900/50 text-red-300',        accent:'red-400'     },
  orange:  { dot:'bg-orange-500',  text:'text-orange-400',  border:'border-orange-800/60',  bg:'bg-orange-950/20',  pill:'bg-orange-900/50 text-orange-300',  accent:'orange-400'  },
  cyan:    { dot:'bg-cyan-500',    text:'text-cyan-400',    border:'border-cyan-800/60',    bg:'bg-cyan-950/20',    pill:'bg-cyan-900/50 text-cyan-300',      accent:'cyan-400'    },
};
const col = (color, key) => COLOR_MAP[color]?.[key] ?? COLOR_MAP.zinc[key];

// ─── Pipeline Corporate hybride ──────────────────────────────────────────────
const CORPORATE_STAGES = [
  { id: 'prospect',      label: 'Prospect',      icon: '🎯', color: 'zinc'    },
  { id: 'qualification', label: 'Qualification',  icon: '🔍', color: 'sky'     },
  { id: 'rdv',           label: 'Rendez-vous',   icon: '📅', color: 'blue'    },
  { id: 'proposition',   label: 'Proposition',   icon: '📄', color: 'amber'   },
  { id: 'negociation',   label: 'Négociation',   icon: '🤝', color: 'orange'  },
  { id: 'contrat',       label: 'Contrat',        icon: '✍️', color: 'purple'  },
  { id: 'livraison',     label: 'Livraison',      icon: '🚀', color: 'cyan'    },
  { id: 'fidelisation',  label: 'Fidélisation',   icon: '⭐', color: 'emerald' },
  { id: 'perdu',         label: 'Perdu',          icon: '❌', color: 'red'     },
];

const SCORE_LABEL_CORPORATE = 'Score Deal';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function useLocalStorageState(key, initial) {
  const [val, setVal] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key) ?? 'null') ?? initial; } catch { return initial; }
  });
  const set = useCallback((v) => {
    const next = typeof v === 'function' ? v(val) : v;
    setVal(next);
    try { localStorage.setItem(key, JSON.stringify(next)); } catch {}
  }, [key, val]);
  return [val, set];
}

function ScoreBar({ score, size = 'md' }) {
  const barColor = score >= 80 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = score >= 80 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400';
  return (
    <div className={`flex items-center gap-2 ${size === 'sm' ? 'w-20' : 'w-28'}`}>
      <div className="flex-1 h-1.5 bg-zinc-800 overflow-hidden">
        <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${Math.min(100, score)}%` }} />
      </div>
      <span className={`text-[10px] font-black font-mono ${textColor} w-6 text-right`}>{score}</span>
    </div>
  );
}

function KanbanColumn({ stage, contacts, onSelect, onMove, onDelete }) {
  const [mode, setMode] = useState('full'); // 'full' | 'compact' | 'collapsed'

  const cycleMode = () => setMode(m => m === 'full' ? 'compact' : m === 'compact' ? 'collapsed' : 'full');

  const isEmpty = contacts.length === 0;

  // On utilise la fonction `col` déjà présente dans le composant parent
  // (ou on l'importe si elle est externe)

  return (
    <div className={`flex flex-col border ${col(stage.color, 'border')} ${col(stage.color, 'bg')} flex-shrink-0 transition-all duration-200 ${
      mode === 'collapsed' ? 'w-10' : mode === 'compact' ? 'w-44' : 'w-52'
    }`}>
      {/* Header */}
      <button
        onClick={cycleMode}
        className={`flex items-center justify-between px-2.5 py-2.5 border-b ${col(stage.color, 'border')} hover:bg-black/20 transition-colors group ${
          mode === 'collapsed' ? 'flex-col gap-2 h-full justify-start pt-3' : ''
        }`}
      >
        {mode === 'collapsed' ? (
          <>
            <span className="text-sm">{stage.icon}</span>
            <div className={`text-[8px] font-black uppercase tracking-widest ${col(stage.color, 'text')} [writing-mode:vertical-rl] rotate-180 leading-none`}>
              {stage.label}
            </div>
            <span className={`text-[9px] font-black tabular-nums ${col(stage.color, 'text')} mt-auto mb-1`}>
              {contacts.length}
            </span>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-sm flex-shrink-0">{stage.icon}</span>
              <span className={`text-[9px] font-black uppercase tracking-wider ${col(stage.color, 'text')} truncate`}>
                {stage.label}
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className={`text-[9px] font-black tabular-nums ${col(stage.color, 'text')}`}>
                {contacts.length}
              </span>
              {/* Indicateur de mode (apparaît au survol) */}
              <div className={`flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity`}>
                {['full','compact','collapsed'].map(m => (
                  <div key={m} className={`w-1 h-1 rounded-full transition-colors ${mode === m ? col(stage.color, 'dot') : 'bg-zinc-700'}`} />
                ))}
              </div>
            </div>
          </>
        )}
      </button>

      {/* Liste des cartes */}
      {mode !== 'collapsed' && (
        <div className={`flex flex-col min-h-[3rem] overflow-y-auto scrollbar-none ${
          mode === 'compact' ? 'gap-0.5 p-1' : 'gap-1.5 p-1.5'
        }`}
          style={{ maxHeight: '32rem' }}
        >
          {contacts.map(ct => (
            <ContactCard
              key={ct._id ?? ct.id}
              contact={ct}
              onClick={onSelect}
              onMove={onMove}
              onDelete={onDelete}
              compact={mode === 'compact'}   // nouvelle prop
            />
          ))}
          {isEmpty && (
            <div className={`text-center text-zinc-800 ${mode === 'compact' ? 'py-3 text-[8px]' : 'py-6 text-[9px]'}`}>
              —
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FieldInput({ k, label, form, onChange, ...props }) {
  return (
    <div>
      <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">{label}</label>
      <input
        value={form[k]}
        onChange={e => onChange(k, e.target.value)}
        {...props}
        className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono"
      />
    </div>
  );
}

// ─── Onglets ─────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'pipeline',    label: 'Pipeline',    icon: Kanban      },
  { key: 'comptes',     label: 'Comptes',     icon: Building2   },
  { key: 'contacts',    label: 'Contacts',    icon: Users       },
  { key: 'finances',    label: 'Finances',    icon: DollarSign  },
  { key: 'documents',   label: 'Documents',   icon: FileText    },
  { key: 'partenaires', label: 'Partenaires', icon: Handshake   },
];

// ─── ContactCard Corporate ────────────────────────────────────────────────────
function ContactCard({ contact, onClick, onMove, onDelete, compact = false }) {
  const stages = CORPORATE_STAGES;
  const currentIdx = stages.findIndex(s => s.id === contact.stage);
  const stage = stages[currentIdx] ?? stages[0];

  return (
    <div
      onClick={() => onClick(contact)}
      className={`group cursor-pointer border border-zinc-800/80 bg-zinc-900/50 hover:bg-zinc-900 hover:border-zinc-700/80 transition-all ${
        compact ? 'p-2 space-y-1' : 'p-3 space-y-2'
      }`}
    >
      {/* Nom + entreprise (compact: seulement nom, pas d'entreprise) */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className={`font-black text-zinc-200 truncate leading-tight uppercase tracking-tight ${
            compact ? 'text-[10px]' : 'text-[11px]'
          }`}>
            {contact.prenom ? `${contact.prenom} ${contact.nom}` : contact.nom}
          </div>
          {!compact && (
            <div className="text-[9px] text-zinc-500 font-mono mt-0.5 flex items-center gap-1 truncate">
              {contact.entreprise
                ? <><Building2 size={8} className="text-sky-500 flex-shrink-0" /><span className="truncate">{contact.entreprise}</span></>
                : <><User size={8} className="flex-shrink-0" /><span>contact indépendant</span></>
              }
              {contact.poste && <span className="text-zinc-700"> · {contact.poste}</span>}
            </div>
          )}
        </div>
        {/* Bouton supprimer (toujours présent, mais plus discret en compact) */}
        <button
          onClick={e => { e.stopPropagation(); onDelete(contact._id ?? contact.id); }}
          className={`opacity-0 group-hover:opacity-100 p-0.5 text-zinc-700 hover:text-red-400 transition-all flex-shrink-0 ${
            compact ? 'absolute top-1 right-1' : ''
          }`}
        >
          <X size={compact ? 8 : 10} />
        </button>
      </div>

      <ScoreBar score={contact.score ?? 0} size={compact ? 'xs' : 'sm'} />

      {/* Valeur deal (cachée en compact) */}
      {!compact && contact.valeur_deal > 0 && (
        <div className="text-[9px] text-emerald-400 font-mono font-bold">
          {Number(contact.valeur_deal).toLocaleString('fr-FR')} €
        </div>
      )}

      {/* Footer nav (compact: seulement les flèches, pas les icônes de contact) */}
      <div className="flex items-center justify-between">
        {!compact && (
          <div className="flex items-center gap-1">
            {contact.email && (
              <a href={`mailto:${contact.email}`} onClick={e => e.stopPropagation()}
                 className="p-1 bg-zinc-800/60 hover:bg-sky-950/60 hover:text-sky-400 text-zinc-600 transition-colors">
                <Mail size={9} />
              </a>
            )}
            {contact.telephone && (
              <a href={`tel:${contact.telephone}`} onClick={e => e.stopPropagation()}
                 className="p-1 bg-zinc-800/60 hover:bg-emerald-950/60 hover:text-emerald-400 text-zinc-600 transition-colors">
                <Phone size={9} />
              </a>
            )}
            {contact.linkedin_url && (
              <a href={contact.linkedin_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                 className="p-1 bg-zinc-800/60 hover:bg-blue-950/60 hover:text-blue-400 text-zinc-600 transition-colors">
                <Linkedin size={9} />
              </a>
            )}
          </div>
        )}
        <div className={`flex items-center gap-0.5 ${compact ? 'ml-auto' : ''}`}>
          {currentIdx > 0 && (
            <button onClick={e => { e.stopPropagation(); onMove(contact._id ?? contact.id, stages[currentIdx - 1].id); }}
                    className="p-0.5 text-zinc-700 hover:text-zinc-300 text-sm leading-none">‹</button>
          )}
          {currentIdx < stages.length - 1 && (
            <button onClick={e => { e.stopPropagation(); onMove(contact._id ?? contact.id, stages[currentIdx + 1].id); }}
                    className="p-0.5 text-zinc-700 hover:text-emerald-400 text-sm leading-none">›</button>
          )}
        </div>
      </div>

      {/* Tags et notes (cachés en compact) */}
      {!compact && contact.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {contact.tags.slice(0, 3).map(tag => (
            <span key={tag} className="text-[8px] px-1 py-0.5 bg-zinc-800/80 text-zinc-500 font-mono">#{tag}</span>
          ))}
        </div>
      )}

      {!compact && contact.notes?.length > 0 && (
        <div className="text-[9px] text-zinc-700 flex items-center gap-1">
          <FileText size={8} /> {contact.notes.length}
        </div>
      )}
    </div>
  );
}

// ─── ContactDrawer Corporate ──────────────────────────────────────────────────
function ContactDrawer({ contact, onClose, onUpdate, onAddNote, onMove }) {
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editScore, setEditScore] = useState(false);
  const [scoreVal, setScoreVal] = useState(contact.score ?? 50);
  const stage = CORPORATE_STAGES.find(s => s.id === contact.stage) ?? CORPORATE_STAGES[0];

  const handleNoteSubmit = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    await onAddNote(contact._id ?? contact.id, newNote.trim());
    setNewNote('');
    setAddingNote(false);
  };

  const fields = [
    { icon: Mail,       key: 'email',          label: 'Email',         href: v => `mailto:${v}` },
    { icon: Phone,      key: 'telephone',      label: 'Tél.',          href: v => `tel:${v}` },
    { icon: Building2,  key: 'entreprise',     label: 'Société',       href: null },
    { icon: Briefcase,  key: 'poste',          label: 'Poste',         href: null },
    { icon: User,       key: 'decideur',       label: 'Décideur',      href: null },
    { icon: Users,      key: 'interlocuteur',  label: 'Opérationnel',  href: null },
    { icon: Landmark,   key: 'siret',          label: 'SIRET',         href: null },
    { icon: Layers,     key: 'secteur',        label: 'Secteur',       href: null },
    { icon: DollarSign, key: 'ca_estime',      label: 'CA client',     href: null, format: v => v ? `${Number(v).toLocaleString('fr-FR')} €` : '—' },
    { icon: Target,     key: 'valeur_deal',    label: 'Valeur deal',   href: null, format: v => v ? `${Number(v).toLocaleString('fr-FR')} €` : '—' },
    { icon: Globe,      key: 'site_web',       label: 'Site web',      href: v => v, external: true },
    { icon: Linkedin,   key: 'linkedin_url',   label: 'LinkedIn',      href: v => v, external: true },
    { icon: ShieldCheck,key: 'contrat_type',   label: 'Contrat',       href: null },
    { icon: FileCheck2, key: 'sla',            label: 'SLA',           href: null },
    { icon: ArrowUpRight,key:'source',         label: 'Source',        href: null },
  ].filter(f => contact[f.key]);

  return (
    <div className="fixed inset-0 z-[90] flex justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0c0f14] border-l border-zinc-800 flex flex-col h-full overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-black/40 shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center text-sm ${col(stage.color, 'bg')} border ${col(stage.color, 'border')}`}>
              {stage.icon}
            </div>
            <div>
              <div className="text-sm font-black text-zinc-100 uppercase tracking-tighter">
                {contact.prenom ? `${contact.prenom} ${contact.nom}` : contact.nom}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[9px] font-mono uppercase ${col(stage.color, 'text')}`}>{stage.label}</span>
                {contact.entreprise && (
                  <><span className="text-zinc-700">·</span>
                  <span className="text-[9px] text-zinc-500 font-mono">{contact.entreprise}</span></>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-zinc-600 hover:text-white transition-colors">
            <X size={15} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto divide-y divide-zinc-800/40">

          {/* Score Deal */}
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">{SCORE_LABEL_CORPORATE}</span>
              <button onClick={() => setEditScore(!editScore)}
                      className="text-[9px] text-zinc-600 hover:text-zinc-400 transition-colors">
                {editScore ? 'Valider' : 'Modifier'}
              </button>
            </div>
            {editScore ? (
              <div className="flex items-center gap-3">
                <input type="range" min={0} max={100} value={scoreVal}
                       onChange={e => setScoreVal(Number(e.target.value))}
                       onMouseUp={() => onUpdate(contact._id ?? contact.id, { score: scoreVal })}
                       className="flex-1 accent-sky-500" />
                <span className="text-sm font-black font-mono text-sky-400 w-8">{scoreVal}</span>
              </div>
            ) : (
              <ScoreBar score={contact.score ?? 0} />
            )}
          </div>

          {/* Pipeline */}
          <div className="px-5 py-4 space-y-2">
            <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">Étape pipeline</span>
            <div className="flex flex-wrap gap-1.5">
              {CORPORATE_STAGES.map(s => (
                <button
                  key={s.id}
                  onClick={() => onMove(contact._id ?? contact.id, s.id)}
                  className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wide border transition-all ${
                    contact.stage === s.id
                      ? `${col(s.color, 'bg')} ${col(s.color, 'border')} ${col(s.color, 'text')} font-black`
                      : 'bg-black/40 border-zinc-800 text-zinc-600 hover:text-zinc-300 hover:border-zinc-700'
                  }`}
                >
                  {s.icon} {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Champs Corporate */}
          {fields.length > 0 && (
            <div className="px-5 py-4">
              <div className="space-y-0 border border-zinc-800/60 bg-black/20 divide-y divide-zinc-800/40">
                {fields.map(({ icon: Icon, key, label, href, format, external }) => (
                  <div key={key} className="flex items-center gap-3 px-3 py-2">
                    <Icon size={12} className="text-zinc-700 flex-shrink-0" />
                    <span className="text-[9px] text-zinc-600 w-20 flex-shrink-0 uppercase tracking-wider">{label}</span>
                    {href ? (
                      <a href={href(contact[key])}
                         target={external ? '_blank' : undefined}
                         rel={external ? 'noreferrer' : undefined}
                         className="text-[10px] text-sky-400 hover:text-sky-300 truncate flex-1 font-mono underline underline-offset-2">
                        {format ? format(contact[key]) : contact[key]}
                      </a>
                    ) : (
                      <span className="text-[10px] text-zinc-300 truncate flex-1 font-mono">
                        {format ? format(contact[key]) : contact[key]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {contact.tags?.length > 0 && (
            <div className="px-5 py-3">
              <div className="flex items-center gap-2 mb-2">
                <Tag size={11} className="text-zinc-700" />
                <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">Tags</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {contact.tags.map(tag => (
                  <span key={tag} className="text-[9px] px-1.5 py-0.5 bg-zinc-800/80 border border-zinc-700/60 text-zinc-500 font-mono">#{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText size={11} className="text-zinc-700" />
                <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">Notes & Suivi</span>
              </div>
              <span className="text-[9px] text-zinc-700 font-mono">{contact.notes?.length ?? 0}</span>
            </div>
            <div className="flex gap-2">
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Compte-rendu RDV, observation, next step…"
                rows={2}
                className="flex-1 bg-black/60 border border-zinc-800 p-2 text-[10px] text-zinc-300 placeholder-zinc-700 focus:border-zinc-600 outline-none resize-none font-mono"
              />
              <button onClick={handleNoteSubmit} disabled={!newNote.trim() || addingNote}
                      className="px-2 bg-sky-950/40 border border-sky-800/40 text-sky-400 hover:bg-sky-950/60 disabled:opacity-30 transition-colors self-end py-1">
                <MessageSquarePlus size={12} />
              </button>
            </div>
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {(contact.notes ?? []).map(note => (
                <div key={note.id} className="px-3 py-2 bg-zinc-900/50 border border-zinc-800/50 space-y-1">
                  <p className="text-[10px] text-zinc-300 leading-relaxed">{note.text}</p>
                  <div className="text-[9px] text-zinc-700 font-mono flex items-center gap-2">
                    <Clock size={8} />
                    {new Date(note.createdAt).toLocaleString('fr-FR')}
                    {note.author && <span>· {note.author}</span>}
                  </div>
                </div>
              ))}
              {!contact.notes?.length && (
                <p className="text-[10px] text-zinc-700 italic text-center py-2">Aucune note</p>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="px-5 py-3 text-[9px] text-zinc-700 font-mono space-y-0.5">
            <div>Créé : {new Date(contact.createdAt).toLocaleString('fr-FR')}</div>
            <div>Modifié : {new Date(contact.updatedAt).toLocaleString('fr-FR')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}



// ─── NewContactModal Corporate ────────────────────────────────────────────────
function NewContactModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    nom: '', prenom: '', email: '', telephone: '',
    poste: '', entreprise: '', siret: '', secteur: '',
    decideur: '', interlocuteur: '', site_web: '', linkedin_url: '',
    ca_estime: '', valeur_deal: '', contrat_type: '', sla: '',
    source: 'inbound',
    stage: CORPORATE_STAGES[0].id, score: 60, tags: '',
  });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.nom.trim()) return;
    setSaving(true);
    await onCreate({
      ...form,
      ca_estime: Number(form.ca_estime) || 0,
      valeur_deal: Number(form.valeur_deal) || 0,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    });
    setSaving(false);
    onClose();
  };
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-zinc-950 border border-zinc-800 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" />
            <h3 className="text-xs font-black uppercase tracking-tighter text-sky-300">Nouveau Contact / Deal</h3>
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors"><X size={14} /></button>
        </div>

        <div className="p-5 space-y-5">

          {/* Identité */}
          <div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-600 mb-2 flex items-center gap-1.5">
              <User size={9} /> Identité
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldInput k="prenom" label="Prénom" form={form} onChange={f} />
              <FieldInput k="nom" label="Nom *" form={form} onChange={f} required />
              <FieldInput k="email" label="Email" form={form} onChange={f} type="email" />
              <FieldInput k="telephone" label="Téléphone" form={form} onChange={f} />
              <FieldInput k="poste" label="Poste / Fonction" form={form} onChange={f} placeholder="DG, DSI, DAF…" />
              <FieldInput k="linkedin_url" label="LinkedIn" form={form} onChange={f} placeholder="https://linkedin.com/in/…" />
            </div>
          </div>

          {/* Société */}
          <div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-600 mb-2 flex items-center gap-1.5">
              <Building2 size={9} /> Société
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldInput k="entreprise" label="Raison sociale" form={form} onChange={f} />
              <FieldInput k="siret" label="SIRET" form={form} onChange={f} placeholder="000 000 000 00000" />
              <FieldInput k="secteur" label="Secteur d'activité" form={form} onChange={f} placeholder="Tech, Finance, Industrie…" />
              <FieldInput k="site_web" label="Site web" form={form} onChange={f} placeholder="https://…" />
              <FieldInput k="ca_estime" label="CA client estimé (€)" form={form} onChange={f} type="number" min={0} />
              <div>
                <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Source</label>
                <select value={form.source} onChange={e => f('source', e.target.value)}
                        className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                  <option value="inbound">Inbound</option>
                  <option value="outbound">Outbound / Cold</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="referral">Référencement</option>
                  <option value="evenement">Événement</option>
                  <option value="partenaire">Partenaire</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
            </div>
          </div>

          {/* Interlocuteurs */}
          <div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-600 mb-2 flex items-center gap-1.5">
              <Users size={9} /> Interlocuteurs
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldInput k="decideur" label="Décideur final" form={form} onChange={f} placeholder="Prénom Nom, titre" />
              <FieldInput k="interlocuteur" label="Interlocuteur opérationnel" form={form} onChange={f} placeholder="Prénom Nom" />
            </div>
          </div>

          {/* Deal */}
          <div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-600 mb-2 flex items-center gap-1.5">
              <Target size={9} /> Deal
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldInput k="valeur_deal" label="Valeur deal (€)" form={form} onChange={f} type="number" min={0} />
              <div>
                <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">
                  {SCORE_LABEL_CORPORATE} ({form.score})
                </label>
                <input type="range" min={0} max={100} value={form.score}
                       onChange={e => f('score', Number(e.target.value))}
                       className="w-full accent-sky-500 mt-2" />
              </div>
              <FieldInput k="contrat_type" label="Type de contrat" form={form} onChange={f} placeholder="MSA, Régie, Forfait…" />
              <FieldInput k="sla" label="SLA / Engagement" form={form} onChange={f} placeholder="99.9%, J+2, 30j…" />
            </div>
          </div>

          {/* Pipeline + Tags */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Étape pipeline</label>
              <select value={form.stage} onChange={e => f('stage', e.target.value)}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                {CORPORATE_STAGES.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
              </select>
            </div>
            <FieldInput k="tags" label="Tags (virgule)" form={form} onChange={f} placeholder="grand-compte, urgent, renouvellement…" />
          </div>

          <button
            onClick={handleSubmit}
            disabled={!form.nom.trim() || saving}
            className="w-full py-3 bg-sky-700 hover:bg-sky-600 disabled:bg-zinc-800 disabled:text-zinc-600 text-white text-[10px] font-black uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
          >
            <UserPlus size={12} />
            {saving ? 'Création…' : 'Créer le contact / deal'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TabComptes ───────────────────────────────────────────────────────────────
function TabComptes({ contacts }) {
  const [search, setSearch] = useState('');

  // Déduplique par entreprise
  const comptes = useMemo(() => {
    const map = {};
    contacts.forEach(ct => {
      if (!ct.entreprise) return;
      if (!map[ct.entreprise]) {
        map[ct.entreprise] = {
          entreprise: ct.entreprise,
          siret: ct.siret,
          secteur: ct.secteur,
          site_web: ct.site_web,
          contacts: [],
          totalDeal: 0,
          maxScore: 0,
        };
      }
      map[ct.entreprise].contacts.push(ct);
      map[ct.entreprise].totalDeal += ct.valeur_deal ?? 0;
      map[ct.entreprise].maxScore = Math.max(map[ct.entreprise].maxScore, ct.score ?? 0);
    });
    return Object.values(map).sort((a, b) => b.totalDeal - a.totalDeal);
  }, [contacts]);

  const filtered = useMemo(() => {
    if (!search.trim()) return comptes;
    const q = search.toLowerCase();
    return comptes.filter(c => c.entreprise.toLowerCase().includes(q) || (c.secteur ?? '').toLowerCase().includes(q));
  }, [comptes, search]);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Rechercher un compte…"
               className="w-full bg-black/60 border border-zinc-800 pl-8 pr-3 py-2 text-[10px] text-zinc-200 placeholder-zinc-700 focus:border-zinc-600 outline-none font-mono" />
      </div>

      <div className="border border-zinc-800 bg-black/40 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-[9px] uppercase tracking-widest text-zinc-600">
          <span className="col-span-4">Compte</span>
          <span className="col-span-2">Secteur</span>
          <span className="col-span-2">Contacts</span>
          <span className="col-span-2">Valeur pipeline</span>
          <span className="col-span-2">Score max</span>
        </div>
        <div className="divide-y divide-zinc-800/30 max-h-[36rem] overflow-y-auto">
          {filtered.map((compte, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-zinc-900/30">
              <div className="col-span-4 min-w-0">
                <div className="text-[10px] font-bold text-zinc-200 truncate">{compte.entreprise}</div>
                {compte.siret && <div className="text-[9px] text-zinc-600 font-mono">{compte.siret}</div>}
              </div>
              <div className="col-span-2 text-[9px] text-zinc-500 font-mono truncate">{compte.secteur || '—'}</div>
              <div className="col-span-2 text-[10px] text-zinc-400 font-mono">{compte.contacts.length}</div>
              <div className="col-span-2 text-[10px] text-emerald-400 font-mono font-bold">
                {compte.totalDeal > 0 ? `${compte.totalDeal.toLocaleString('fr-FR')} €` : '—'}
              </div>
              <div className="col-span-2">
                <ScoreBar score={compte.maxScore} size="sm" />
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <Building2 size={28} className="text-zinc-800 mx-auto mb-2" />
              <p className="text-zinc-600 text-[10px] uppercase tracking-widest">Aucun compte</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── TabContacts (liste plate) ────────────────────────────────────────────────
function TabContacts({ contacts, onSelect }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search.trim()) return contacts;
    const q = search.toLowerCase();
    return contacts.filter(ct =>
      [ct.nom, ct.prenom, ct.email, ct.entreprise, ct.poste].filter(Boolean).some(v => v.toLowerCase().includes(q))
    );
  }, [contacts, search]);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Rechercher un contact…"
               className="w-full bg-black/60 border border-zinc-800 pl-8 pr-3 py-2 text-[10px] text-zinc-200 placeholder-zinc-700 focus:border-zinc-600 outline-none font-mono" />
      </div>

      <div className="border border-zinc-800 bg-black/40">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-[9px] uppercase tracking-widest text-zinc-600">
          <span className="col-span-3">Contact</span>
          <span className="col-span-2">Poste</span>
          <span className="col-span-2">Société</span>
          <span className="col-span-2">Étape</span>
          <span className="col-span-2">Deal</span>
          <span className="col-span-1">Score</span>
        </div>
        <div className="divide-y divide-zinc-800/30 max-h-[36rem] overflow-y-auto">
          {filtered.map((ct, idx) => {
            const stage = CORPORATE_STAGES.find(s => s.id === ct.stage) ?? CORPORATE_STAGES[0];
            return (
              <div key={ct._id ?? idx} onClick={() => onSelect(ct)}
                   className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-zinc-900/30 cursor-pointer">
                <div className="col-span-3 min-w-0">
                  <div className="text-[10px] font-bold text-zinc-200 truncate">
                    {ct.prenom ? `${ct.prenom} ${ct.nom}` : ct.nom}
                  </div>
                  <div className="text-[9px] text-zinc-600 font-mono truncate">{ct.email}</div>
                </div>
                <div className="col-span-2 text-[9px] text-zinc-500 truncate">{ct.poste || '—'}</div>
                <div className="col-span-2 text-[9px] text-zinc-500 truncate">{ct.entreprise || '—'}</div>
                <div className="col-span-2">
                  <span className={`text-[8px] px-1 py-0.5 font-mono uppercase ${col(stage.color, 'pill')}`}>
                    {stage.icon} {stage.label}
                  </span>
                </div>
                <div className="col-span-2 text-[9px] text-emerald-400 font-mono">
                  {ct.valeur_deal > 0 ? `${Number(ct.valeur_deal).toLocaleString('fr-FR')} €` : '—'}
                </div>
                <div className="col-span-1 text-[9px] font-mono text-zinc-400">{ct.score ?? 0}</div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-12 text-center">
              <User size={28} className="text-zinc-800 mx-auto mb-2" />
              <p className="text-zinc-600 text-[10px] uppercase tracking-widest">Aucun contact</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── TabFinances ──────────────────────────────────────────────────────────────
function TabFinances({ finances, setFinances }) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleSave = (data) => {
    if (editing) {
      setFinances(p => p.map(f => f.id === editing.id ? { ...f, ...data, updatedAt: new Date().toISOString() } : f));
    } else {
      setFinances(p => [{ id: `fin_${Date.now()}`, ...data, createdAt: new Date().toISOString() }, ...p]);
    }
    setShowModal(false);
    setEditing(null);
  };

  const totals = useMemo(() => ({
    revenus:   finances.filter(f => f.type === 'revenu').reduce((a, f) => a + (f.montant ?? 0), 0),
    depenses:  finances.filter(f => f.type === 'depense').reduce((a, f) => a + (f.montant ?? 0), 0),
    commandes: finances.filter(f => f.type === 'commande').reduce((a, f) => a + (f.montant ?? 0), 0),
  }), [finances]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Revenus',   value: totals.revenus,                          color: 'text-emerald-400' },
          { label: 'Commandes', value: totals.commandes,                         color: 'text-sky-400'     },
          { label: 'Dépenses',  value: totals.depenses,                          color: 'text-red-400'     },
          { label: 'Balance',   value: totals.revenus - totals.depenses,         color: (totals.revenus - totals.depenses) >= 0 ? 'text-emerald-400' : 'text-red-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="border border-zinc-800 bg-black/40 p-3">
            <div className="text-[9px] uppercase tracking-widest text-zinc-600">{label}</div>
            <div className={`text-xl font-black font-mono ${color}`}>{value.toLocaleString('fr-FR')} €</div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center">
        <span className="text-[9px] uppercase tracking-widest text-zinc-600">
          {finances.length} transaction{finances.length > 1 ? 's' : ''}
        </span>
        <button onClick={() => { setEditing(null); setShowModal(true); }}
                className="flex items-center gap-1.5 px-3 py-2 bg-sky-700/70 hover:bg-sky-700 text-white text-[9px] uppercase tracking-widest font-bold transition-colors">
          <Plus size={11} /> Ajouter
        </button>
      </div>

      <div className="border border-zinc-800 bg-black/40">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-[9px] uppercase tracking-widest text-zinc-600">
          <span className="col-span-4">Description</span>
          <span className="col-span-2">Type</span>
          <span className="col-span-2">Montant</span>
          <span className="col-span-2">Date</span>
          <span className="col-span-1">Statut</span>
          <span className="col-span-1 text-right">···</span>
        </div>
        <div className="divide-y divide-zinc-800/30 max-h-[32rem] overflow-y-auto">
          {finances.map(f => (
            <div key={f.id} onClick={() => { setEditing(f); setShowModal(true); }}
                 className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-zinc-900/30 cursor-pointer">
              <div className="col-span-4 text-[10px] font-bold text-zinc-200 truncate">{f.description}</div>
              <div className="col-span-2">
                <span className={`text-[9px] px-1.5 py-0.5 font-mono ${
                  f.type === 'revenu'   ? 'bg-emerald-900/50 text-emerald-300'
                  : f.type === 'commande' ? 'bg-sky-900/50 text-sky-300'
                  : 'bg-red-900/50 text-red-300'
                }`}>
                  {f.type === 'revenu' ? '↑' : f.type === 'commande' ? '⬛' : '↓'} {f.type}
                </span>
              </div>
              <div className="col-span-2 text-[10px] font-mono font-bold text-zinc-300">
                {Number(f.montant).toLocaleString('fr-FR')} €
              </div>
              <div className="col-span-2 text-[9px] text-zinc-600 font-mono">{f.date}</div>
              <div className="col-span-1">
                <span className={`text-[9px] font-mono ${
                  f.statut === 'payé'       ? 'text-emerald-400'
                  : f.statut === 'facturé'  ? 'text-sky-400'
                  : f.statut === 'en_attente' ? 'text-amber-400'
                  : 'text-zinc-600'
                }`}>{f.statut}</span>
              </div>
              <div className="col-span-1 flex justify-end">
                <button onClick={e => { e.stopPropagation(); setFinances(p => p.filter(x => x.id !== f.id)); }}
                        className="p-1 text-zinc-700 hover:text-red-400 transition-colors"><Trash2 size={10} /></button>
              </div>
            </div>
          ))}
          {finances.length === 0 && (
            <div className="py-14 text-center">
              <DollarSign size={28} className="text-zinc-800 mx-auto mb-2" />
              <p className="text-zinc-600 text-[10px]">Aucune transaction</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <FinanceModal item={editing} onSave={handleSave} onClose={() => { setShowModal(false); setEditing(null); }} />
      )}
    </div>
  );
}

function FinanceModal({ item, onSave, onClose }) {
  const [form, setForm] = useState({
    description: item?.description || '', type: item?.type || 'revenu',
    montant: item?.montant || '', date: item?.date || new Date().toISOString().split('T')[0],
    statut: item?.statut || 'en_attente', compte: item?.compte || '', ref_commande: item?.ref_commande || '',
  });
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-zinc-950 border border-zinc-800 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-black/30">
          <h3 className="text-xs font-black uppercase tracking-tighter text-sky-300">
            {item ? 'Modifier' : 'Ajouter'} une transaction
          </h3>
          <button onClick={onClose} className="text-zinc-600 hover:text-white"><X size={13} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Description *</label>
            <input value={form.description} onChange={e => f('description', e.target.value)}
                   className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Type</label>
              <select value={form.type} onChange={e => f('type', e.target.value)}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                <option value="revenu">↑ Revenu</option>
                <option value="commande">⬛ Commande</option>
                <option value="depense">↓ Dépense</option>
              </select>
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Montant (€)</label>
              <input type="number" value={form.montant} onChange={e => f('montant', e.target.value)}
                     className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => f('date', e.target.value)}
                     className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Statut</label>
              <select value={form.statut} onChange={e => f('statut', e.target.value)}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                <option value="en_attente">En attente</option>
                <option value="facturé">Facturé</option>
                <option value="payé">Payé</option>
                <option value="litige">Litige</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Compte client</label>
              <input value={form.compte} onChange={e => f('compte', e.target.value)} placeholder="Raison sociale…"
                     className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Réf. commande</label>
              <input value={form.ref_commande} onChange={e => f('ref_commande', e.target.value)} placeholder="BC-2024-001"
                     className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={() => onSave({ ...form, montant: Number(form.montant) || 0 })}
                    disabled={!form.description.trim()}
                    className="flex-1 py-2.5 bg-sky-700 hover:bg-sky-600 disabled:bg-zinc-800 text-white text-[10px] font-black uppercase tracking-widest">
              {item ? 'Enregistrer' : 'Créer'}
            </button>
            <button onClick={onClose} className="flex-1 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 text-[10px] uppercase tracking-widest">
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TabDocuments ─────────────────────────────────────────────────────────────
function TabDocuments({ documents, setDocuments }) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const DOC_TYPES = ['Contrat', 'Devis', 'Bon de commande', 'Facture', 'Avenant', 'NDA', 'Proposition', 'Rapport', 'Autre'];

  const handleSave = (data) => {
    if (editing) {
      setDocuments(p => p.map(d => d.id === editing.id ? { ...d, ...data } : d));
    } else {
      setDocuments(p => [{ id: `doc_${Date.now()}`, ...data, createdAt: new Date().toISOString() }, ...p]);
    }
    setShowModal(false);
    setEditing(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-[9px] uppercase tracking-widest text-zinc-600">
          {documents.length} document{documents.length > 1 ? 's' : ''}
        </span>
        <button onClick={() => { setEditing(null); setShowModal(true); }}
                className="flex items-center gap-1.5 px-3 py-2 bg-sky-700/70 hover:bg-sky-700 text-white text-[9px] uppercase tracking-widest font-bold transition-colors">
          <Plus size={11} /> Référencer
        </button>
      </div>

      <div className="border border-zinc-800 bg-black/40">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-[9px] uppercase tracking-widest text-zinc-600">
          <span className="col-span-4">Document</span>
          <span className="col-span-2">Type</span>
          <span className="col-span-2">Compte</span>
          <span className="col-span-2">Date</span>
          <span className="col-span-1">Statut</span>
          <span className="col-span-1 text-right">···</span>
        </div>
        <div className="divide-y divide-zinc-800/30 max-h-[36rem] overflow-y-auto">
          {documents.map(doc => (
            <div key={doc.id} onClick={() => { setEditing(doc); setShowModal(true); }}
                 className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-zinc-900/30 cursor-pointer">
              <div className="col-span-4 min-w-0">
                <div className="text-[10px] font-bold text-zinc-200 truncate">{doc.nom}</div>
                {doc.lien && (
                  <a href={doc.lien} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                     className="text-[9px] text-sky-500 hover:text-sky-400 flex items-center gap-0.5">
                    <ExternalLink size={8} /> Ouvrir
                  </a>
                )}
              </div>
              <div className="col-span-2">
                <span className="text-[9px] px-1.5 py-0.5 bg-zinc-800 text-zinc-400 font-mono">{doc.type || '—'}</span>
              </div>
              <div className="col-span-2 text-[9px] text-zinc-500 truncate">{doc.compte || '—'}</div>
              <div className="col-span-2 text-[9px] text-zinc-600 font-mono">{doc.date || '—'}</div>
              <div className="col-span-1">
                <span className={`text-[9px] font-mono ${
                  doc.statut === 'signé'    ? 'text-emerald-400'
                  : doc.statut === 'validé' ? 'text-sky-400'
                  : doc.statut === 'brouillon' ? 'text-amber-400'
                  : 'text-zinc-600'
                }`}>{doc.statut || '—'}</span>
              </div>
              <div className="col-span-1 flex justify-end">
                <button onClick={e => { e.stopPropagation(); setDocuments(p => p.filter(x => x.id !== doc.id)); }}
                        className="p-1 text-zinc-700 hover:text-red-400 transition-colors"><Trash2 size={10} /></button>
              </div>
            </div>
          ))}
          {documents.length === 0 && (
            <div className="py-14 text-center">
              <FileText size={28} className="text-zinc-800 mx-auto mb-2" />
              <p className="text-zinc-600 text-[10px]">Aucun document référencé</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <DocumentModal item={editing} types={DOC_TYPES} onSave={handleSave} onClose={() => { setShowModal(false); setEditing(null); }} />
      )}
    </div>
  );
}

function DocumentModal({ item, types, onSave, onClose }) {
  const [form, setForm] = useState({
    nom: item?.nom || '', type: item?.type || 'Contrat',
    compte: item?.compte || '', lien: item?.lien || '',
    date: item?.date || new Date().toISOString().split('T')[0],
    statut: item?.statut || 'brouillon',
  });
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-zinc-950 border border-zinc-800 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-black/30">
          <h3 className="text-xs font-black uppercase tracking-tighter text-sky-300">
            {item ? 'Modifier' : 'Référencer'} un document
          </h3>
          <button onClick={onClose} className="text-zinc-600 hover:text-white"><X size={13} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Nom *</label>
            <input value={form.nom} onChange={e => f('nom', e.target.value)}
                   className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Type</label>
              <select value={form.type} onChange={e => f('type', e.target.value)}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                {types.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Statut</label>
              <select value={form.statut} onChange={e => f('statut', e.target.value)}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                <option value="brouillon">Brouillon</option>
                <option value="envoyé">Envoyé</option>
                <option value="validé">Validé</option>
                <option value="signé">Signé</option>
                <option value="archivé">Archivé</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Compte client</label>
            <input value={form.compte} onChange={e => f('compte', e.target.value)} placeholder="Raison sociale…"
                   className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Lien (URL ou chemin)</label>
            <input value={form.lien} onChange={e => f('lien', e.target.value)} placeholder="https://drive.google.com/…"
                   className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Date</label>
            <input type="date" value={form.date} onChange={e => f('date', e.target.value)}
                   className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={() => onSave(form)} disabled={!form.nom.trim()}
                    className="flex-1 py-2.5 bg-sky-700 hover:bg-sky-600 disabled:bg-zinc-800 text-white text-[10px] font-black uppercase tracking-widest">
              {item ? 'Enregistrer' : 'Référencer'}
            </button>
            <button onClick={onClose} className="flex-1 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 text-[10px] uppercase tracking-widest">
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TabPartenaires ───────────────────────────────────────────────────────────
function TabPartenaires() {
  const partners = [
    {
      id: 'spondei', nom: 'Spondei', description: 'CRM partenaire — transfert de contacts et opportunités B2B',
      lien: 'https://spondei.com', color: 'sky', badge: 'Connecté',
    },
    {
      id: 'linkedin', nom: 'LinkedIn Sales Nav.', description: 'Prospection et enrichissement de leads B2B',
      lien: 'https://www.linkedin.com/sales', color: 'blue', badge: 'Externe',
    },
    {
      id: 'docusign', nom: 'DocuSign', description: 'Signature électronique de contrats et avenants',
      lien: 'https://www.docusign.com', color: 'amber', badge: 'Externe',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="text-[9px] uppercase tracking-widest text-zinc-600">{partners.length} partenaires</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {partners.map(p => (
          <div key={p.id}
               className={`border bg-black/40 p-4 transition-colors border-zinc-800 hover:bg-zinc-900/30 hover:border-zinc-700`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className={`text-xs font-black uppercase tracking-tight ${col(p.color, 'text')}`}>{p.nom}</h3>
                  {p.badge && (
                    <span className={`text-[8px] px-1.5 py-0.5 font-mono uppercase ${col(p.color, 'pill')}`}>{p.badge}</span>
                  )}
                </div>
                <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed">{p.description}</p>
              </div>
              <a href={p.lien} target="_blank" rel="noreferrer"
                 className={`p-2 border transition-colors flex-shrink-0 ${col(p.color, 'bg')} ${col(p.color, 'border')} ${col(p.color, 'text')} hover:opacity-80`}>
                <ExternalLink size={13} />
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────────
export default function CRMCorporatePanel({ darkMode = true }) {
  const {
    activityProfile,
    crmContacts, crmLoading, crmSearch, crmStageFilter,
    selectedContact,
    fetchCrmContacts, createCrmContact, updateCrmContact,
    deleteCrmContact, moveCrmContactStage, addCrmNote,
    setCrmSearch, setCrmStageFilter, setSelectedContact,
    clearSelectedContact, filteredCrmContacts,
  } = useGrandeEcole();

  const [activeTab, setActiveTab]   = useState('pipeline');
  const [showNew, setShowNew]       = useState(false);

  const [finances,  setFinances]  = useLocalStorageState('crm_corporate_finances',  []);
  const [documents, setDocuments] = useLocalStorageState('crm_corporate_documents', []);

  const filtered = useMemo(
    () => filteredCrmContacts?.() ?? crmContacts,
    [crmContacts, crmSearch, crmStageFilter, filteredCrmContacts]
  );

  const byStage = useMemo(() => {
    const map = {};
    CORPORATE_STAGES.forEach(s => { map[s.id] = []; });
    filtered.forEach(ct => {
      const key = CORPORATE_STAGES.find(s => s.id === ct.stage) ? ct.stage : CORPORATE_STAGES[0].id;
      map[key].push(ct);
    });
    return map;
  }, [filtered]);

  // KPIs
  const kpis = useMemo(() => {
    const total      = crmContacts.length;
    const actifs     = crmContacts.filter(ct => !['perdu'].includes(ct.stage)).length;
    const contrats   = crmContacts.filter(ct => ct.stage === 'contrat').length;
    const pipeline   = crmContacts.reduce((a, ct) => a + (ct.valeur_deal ?? 0), 0);
    const avgScore   = total > 0
      ? Math.round(crmContacts.reduce((a, ct) => a + (ct.score ?? 0), 0) / total)
      : 0;
    return { total, actifs, contrats, pipeline, avgScore };
  }, [crmContacts]);

  useEffect(() => { fetchCrmContacts(); }, []);


  return (
    <div className="space-y-5 font-mono">

      {/* ── ONGLETS ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap border-b border-zinc-800">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-[10px] uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === key
                      ? 'border-sky-500 text-sky-300 bg-sky-950/15'
                      : 'border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900/20'
                  }`}>
            <Icon size={12} />
            {label}
          </button>
        ))}
      </div>

      {/* ── PIPELINE ────────────────────────────────────────────────── */}
      {activeTab === 'pipeline' && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'Contacts',  value: kpis.total,                      color: 'text-zinc-300',    icon: User,        sub: `${kpis.actifs} actifs` },
              { label: 'Contrats',  value: kpis.contrats,                   color: 'text-purple-400',  icon: BadgeCheck,  sub: 'étape contrat' },
              { label: 'Pipeline',  value: `${(kpis.pipeline / 1000).toFixed(0)}k €`, color: 'text-emerald-400', icon: TrendingUp, sub: 'valeur totale' },
              { label: SCORE_LABEL_CORPORATE, value: kpis.avgScore,         color: kpis.avgScore >= 70 ? 'text-emerald-400' : kpis.avgScore >= 40 ? 'text-amber-400' : 'text-red-400', icon: Star, sub: 'moyen' },
              { label: 'Étapes',    value: CORPORATE_STAGES.length,         color: 'text-sky-400',     icon: Layers,      sub: 'pipeline hybride' },
            ].map(({ label, value, color, icon: Icon, sub }) => (
              <div key={label} className="border border-zinc-800 bg-black/40 p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] uppercase tracking-widest text-zinc-600">{label}</span>
                  <Icon size={12} className={`${color} opacity-50`} />
                </div>
                <div className={`text-xl font-black font-mono ${color}`}>{value}</div>
                <div className="text-[9px] text-zinc-600 mt-0.5 truncate">{sub}</div>
              </div>
            ))}
          </div>

          {/* Barre de recherche + actions */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-36">
              <Search size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input value={crmSearch} onChange={e => setCrmSearch(e.target.value)}
                     placeholder="Rechercher contact, société, poste…"
                     className="w-full bg-black/60 border border-zinc-800 pl-8 pr-3 py-2 text-[10px] text-zinc-200 placeholder-zinc-700 focus:border-zinc-600 outline-none" />
            </div>
            <div className="relative">
              <Filter size={10} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
              <select value={crmStageFilter} onChange={e => setCrmStageFilter(e.target.value)}
                      className="bg-black/60 border border-zinc-800 pl-7 pr-7 py-2 text-[10px] text-zinc-300 focus:border-zinc-600 outline-none appearance-none">
                <option value="all">Toutes étapes</option>
                {CORPORATE_STAGES.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
              </select>
              <ChevronDown size={9} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
            </div>
            <button onClick={() => setShowNew(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-sky-700/80 hover:bg-sky-700 text-white text-[9px] uppercase tracking-widest font-bold transition-colors">
              <Plus size={11} />
              <span className="hidden sm:inline">Nouveau deal</span>
            </button>
          </div>

          <div className="text-[9px] text-zinc-700 uppercase tracking-widest -mt-1">
            {filtered.length} contact{filtered.length > 1 ? 's' : ''}
          </div>

          <div className="overflow-x-auto pb-3">
  <div className="flex gap-2.5 min-w-max">
    {CORPORATE_STAGES.map(stage => {
      const cols = byStage[stage.id] ?? [];
      return (
        <KanbanColumn
          key={stage.id}
          stage={stage}
          contacts={cols}
          onSelect={setSelectedContact}
          onMove={moveCrmContactStage}
          onDelete={deleteCrmContact}
        />
      );
    })}
  </div>
</div>
        </>
      )}

      {/* ── COMPTES ─────────────────────────────────────────────────── */}
      {activeTab === 'comptes' && <TabComptes contacts={crmContacts} />}

      {/* ── CONTACTS liste ──────────────────────────────────────────── */}
      {activeTab === 'contacts' && (
        <TabContacts contacts={filtered} onSelect={setSelectedContact} />
      )}

      {/* ── FINANCES ────────────────────────────────────────────────── */}
      {activeTab === 'finances' && (
        <TabFinances finances={finances} setFinances={setFinances} />
      )}

      {/* ── DOCUMENTS ───────────────────────────────────────────────── */}
      {activeTab === 'documents' && (
        <TabDocuments documents={documents} setDocuments={setDocuments} />
      )}

      {/* ── PARTENAIRES ─────────────────────────────────────────────── */}
      {activeTab === 'partenaires' && <TabPartenaires />}

      {/* ── DRAWER CONTACT ──────────────────────────────────────────── */}
      {selectedContact && (
        <ContactDrawer
          contact={selectedContact}
          onClose={clearSelectedContact}
          onUpdate={updateCrmContact}
          onAddNote={addCrmNote}
          onMove={moveCrmContactStage}
        />
      )}

      {/* ── MODAL NOUVEAU CONTACT ───────────────────────────────────── */}
      {showNew && (
        <NewContactModal
          onClose={() => setShowNew(false)}
          onCreate={createCrmContact}
        />
      )}
    </div>
  );
}